import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Check if we're in dev mode (same check as sessionGuard)
const DEV_MODE = true;

export function usePermissions() {
  const [permissions, setPermissions] = useState<string[]>([]);
  const [permissionCount, setPermissionCount] = useState<{ granted: number; total: number } | null>(null);
  const [loading, setLoading] = useState(!DEV_MODE); // Don't load in dev mode

  useEffect(() => {
    if (!DEV_MODE) {
      loadPermissions();
    }
  }, []);

  const loadPermissions = async () => {
    try {
      setLoading(true);

      // Get permission count using RPC
      const { data: countData, error: countError } = await (supabase as any).rpc('get_user_perm_count');
      
      // Gracefully handle missing database resources
      if (countError) {
        if (countError.code === 'PGRST202' || countError.code === 'PGRST205') {
          console.log('Permission system not yet configured - using defaults');
          return;
        }
      }
      
      if (!countError && countData) {
        setPermissionCount(Array.isArray(countData) ? countData[0] : countData);
      }

      // Get user's role first
      const { data: membershipData, error: membershipError } = await (supabase as any)
        .from('memberships')
        .select('role')
        .single();

      // Gracefully handle missing tables
      if (membershipError) {
        if (membershipError.code === 'PGRST205') {
          console.log('Membership system not yet configured - using defaults');
          return;
        }
      }

      if (!membershipError && membershipData?.role) {
        // Get permissions for that role
        const { data: permsData, error: permsError } = await (supabase as any)
          .from('role_permissions')
          .select('permission_key')
          .eq('role', membershipData.role);

        if (!permsError && permsData) {
          setPermissions(permsData.map((p: any) => p.permission_key));
        }
      }
    } catch (error) {
      console.error('Failed to load permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = (permission: string): boolean => {
    // In dev mode, grant all permissions
    if (DEV_MODE) return true;
    return permissions.includes(permission);
  };

  return { permissions, permissionCount, hasPermission, loading, refresh: loadPermissions };
}
